import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { api } from "_shared/_stores/api";
import type { AppDispatch } from "_shared/_stores/store-types";
import Guid from "_shared/guids/guid";
import {
  BulkDocumentRequest,
  createBulkDocumentRequests,
  DocumentRequestFileTypes,
} from "documents/_api/commands/documents-form-api-request";
import { documentListTag } from "documents/_api/document-api";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import Document from "documents/document";
import DocumentTopic from "documents/topics/document-topic";

/**
 * A work agreement's own documents (#6052, #6054) are ordinary Documents-center documents carrying
 * the work's topic. They are listed with `GET /documents` filtered by that topic, uploaded with
 * `POST /documents`, and taken off the work by replacing their topics. This module only says what
 * the topic is and builds those requests; the Documents API does the rest, and the server decides
 * who may give a document the topic.
 */

/** The topic that puts a document on a work, named by its conversation: the first agreement in its revision chain. */
export const WORK_DOCUMENTS_ENTITY_CLASS = "Work.Agreement";
export const WORK_DOCUMENTS_CONTEXT = "WorkDocuments";

/** The most files one upload may carry. */
export const MAX_FILES_PER_WORK_DOCUMENT_UPLOAD = 20;

/** The types a work document may be. Mirrors `WorkAgreementDocumentUploadPolicy`; the server decides. */
export const WORK_DOCUMENT_FILE_TYPES: DocumentRequestFileTypes = {
  mediaTypes: new Set([
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.oasis.opendocument.text",
    "text/plain",
    "text/csv",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.oasis.opendocument.spreadsheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.oasis.opendocument.presentation",
    // Every type a chat attachment may be (#6055).
    "image/png",
    "image/jpeg",
    "image/gif",
    "image/bmp",
    "image/webp",
    "image/heic",
    "image/heif",
    "image/svg+xml",
    "application/zip",
  ]),
  unsupportedTypeIssue:
    "This file type is not supported. Choose a PDF, Word, OpenDocument, text, CSV, spreadsheet, " +
    "PowerPoint, image, SVG, or zip file.",
};

export function workDocumentsTopic(conversationId: Guid): DocumentTopic {
  return DocumentTopic.create({
    entityClass: WORK_DOCUMENTS_ENTITY_CLASS,
    entityId: conversationId,
    context: WORK_DOCUMENTS_CONTEXT,
  });
}

/** Whether a topic is this conversation's work-documents topic. */
export function isWorkDocumentsTopic(topic: DocumentTopic, conversationId: Guid): boolean {
  return (
    topic.entityClass === WORK_DOCUMENTS_ENTITY_CLASS &&
    topic.context === WORK_DOCUMENTS_CONTEXT &&
    Boolean(topic.entityId?.isEqualTo(conversationId))
  );
}

/** The size of one page of the work's documents. */
export const WORK_DOCUMENTS_PAGE_SIZE = 25;

/**
 * One page of the work's documents, newest first. Fixed pages, fetched one after another, rather
 * than one page that grows: the server caps a page's size, so a growing page stops at the cap.
 */
export function workDocumentsQuery(conversationId: Guid, pageIndex: number, pageSize = WORK_DOCUMENTS_PAGE_SIZE): DocumentQuery {
  return new DocumentQuery({
    filterParameters: new DocumentFilterParameters({
      entityClass: WORK_DOCUMENTS_ENTITY_CLASS,
      entityId: conversationId,
      contexts: [WORK_DOCUMENTS_CONTEXT],
    }),
    orderParameters: new DocumentOrderParameters({
      orderBy: DocumentOrderField.Created,
      orderDir: OrderDirection.Descending,
    }),
    paginationParameters: PaginationParameters.create({ pageIndex, pageSize }),
  });
}

/** The upload requests that put these files on the work. */
export function workDocumentUploadRequests(files: File[], conversationId: Guid): BulkDocumentRequest[] {
  return createBulkDocumentRequests(files.map(workDocumentUploadFile), {
    topics: [workDocumentsTopic(conversationId)],
    fileTypes: WORK_DOCUMENT_FILE_TYPES,
  });
}

/** The document as it is once taken off the work: every other topic kept. */
export function withoutWorkTopic(document: Document, conversationId: Guid): Document {
  return document.withTopics(document.topics.filter((topic) => !isWorkDocumentsTopic(topic, conversationId)));
}

/** The type a file is sent as, when what the browser says is not what the server checks for. */
const RETYPED_EXTENSIONS: Record<string, { type: string; when: (declared: string) => boolean }> = {
  // A browser on Windows with Excel installed declares a `.csv` as `application/vnd.ms-excel`, and
  // the server checks a declaration against the file's own bytes: a CSV is text, so it would be
  // refused as a mismatch. Sent as `text/csv`, it is accepted and stored as plain text.
  csv: { type: "text/csv", when: (declared) => declared !== "text/csv" },
  // Windows browsers declare a zip as `application/x-zip-compressed`, which no list names (#6055).
  zip: { type: "application/zip", when: (declared) => declared === "" || declared === "application/x-zip-compressed" },
  // And often declare a HEIC or HEIF photo as nothing at all.
  heic: { type: "image/heic", when: (declared) => declared === "" },
  heif: { type: "image/heif", when: (declared) => declared === "" },
};

export function workDocumentUploadFile(file: File): File {
  const extension = file.name.slice(file.name.lastIndexOf(".") + 1).toLocaleLowerCase();
  const retyped = file.name.includes(".") ? RETYPED_EXTENSIONS[extension] : undefined;
  if (!retyped?.when(file.type.toLowerCase())) return file;
  return new File([file], file.name, { type: retyped.type, lastModified: file.lastModified });
}

/**
 * Asks again for the documents lists when the server says a work's documents changed. The lists are
 * the Documents API's, tagged by the Documents list; which work changed does not narrow it.
 */
export const invalidateWorkAgreementDocuments = (dispatch: AppDispatch) => {
  dispatch(api.util.invalidateTags([documentListTag]));
};
